// components/NavigationShell.tsx
// A navigation shell with sidebar/topbar that wraps Plasmic content via a slot.
// Uses SSR data from PageWrapper's layout context.

import React, { useState, useMemo, useEffect, ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { DataProvider } from "@plasmicapp/react-web/lib/host";
import {
  ALL_PANELS,
  buildNavigation,
  BuiltNavGroup,
  BuiltNavItem,
} from "../lib/navigation-config";

// ─── Types ──────────────────────────────────────────────────────────────────

interface NavigationShellProps {
  /** Layout data from SSR (get_current_technician RPC result) */
  layoutData?: {
    panel_access?: string[];
    panel_access_data?: Record<string, Record<string, any>>;
    technician_name?: string;
    [key: string]: any;
  };
  /** Plasmic slot for page content */
  children?: ReactNode;
  /** Optional className for the root container */
  className?: string;
  /** Whether sidebar starts collapsed */
  defaultCollapsed?: boolean;
  /** Position: "left" for sidebar, "top" for topbar */
  position?: "left" | "top";
}

// ─── Colors ─────────────────────────────────────────────────────────────────

const C = {
  bg: "#ffffff",
  bgSidebar: "#01303F",
  bgHover: "rgba(255,255,255,0.08)",
  bgActive: "rgba(255,255,255,0.15)",
  text: "rgba(255,255,255,0.7)",
  textActive: "#ffffff",
  textMuted: "rgba(255,255,255,0.45)",
  border: "rgba(255,255,255,0.1)",
  accent: "#ffffff",
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    alignItems: "stretch",
    minHeight: "100vh",
    background: "#ffffff",
    color: C.text,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  rootTop: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    background: "#ffffff",
    color: C.text,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  },
  sidebar: {
    width: 260,
    background: C.bgSidebar,
    borderRight: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    // sticky keeps the sidebar in view while scrolling, but the sidebar itself
    // fills the full column height (minHeight 100vh) so it never abruptly stops
    // when content is taller than the viewport.
    position: "sticky",
    top: 0,
    height: "100vh",
    minHeight: "100%",
    overflowY: "auto",
    transition: "width 0.2s ease",
  },
  sidebarCollapsed: {
    width: 60,
  },
  topbar: {
    background: C.bgSidebar,
    borderBottom: `1px solid ${C.border}`,
    padding: "0 16px",
    height: 56,
    display: "flex",
    alignItems: "center",
    gap: 8,
    overflowX: "visible",
    overflowY: "visible",
    position: "relative",
    zIndex: 1000,
  },
  header: {
    padding: "16px",
    borderBottom: `1px solid ${C.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: {
    fontWeight: 700,
    fontSize: 18,
    color: "#ffffff",
  },
  collapseBtn: {
    background: "transparent",
    border: "none",
    color: C.textMuted,
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navSection: {
    padding: "12px 0",
  },
  groupTitle: {
    padding: "8px 16px 4px",
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "rgba(255,255,255,0.7)",
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 16px",
    color: C.text,
    textDecoration: "none",
    fontSize: 14,
    transition: "background 0.15s, color 0.15s",
    cursor: "pointer",
    borderRadius: 6,
    margin: "1px 8px",
  },
  navItemActive: {
    background: C.bgActive,
    color: "#ffffff",
  },
  navItemHover: {
    background: C.bgHover,
    color: "#ffffff",
  },
  badge: {
    marginLeft: "auto",
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 6px",
    borderRadius: 10,
    minWidth: 20,
    textAlign: "center",
  },
  content: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    background: "#ffffff",
    color: "#111111",
  },
  mobileTopbar: {
    position: "sticky" as const,
    top: 0,
    zIndex: 100,
    height: 52,
    background: C.bgSidebar,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    borderBottom: `1px solid ${C.border}`,
  },
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 200,
  },
  drawer: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    bottom: 0,
    width: 280,
    background: C.bgSidebar,
    zIndex: 201,
    display: "flex",
    flexDirection: "column" as const,
    overflowY: "auto" as const,
    boxShadow: "4px 0 24px rgba(0,0,0,0.3)",
    transition: "transform 0.25s ease",
  },
  drawerClose: {
    background: "transparent",
    border: "none",
    color: C.textMuted,
    cursor: "pointer",
    padding: 4,
    borderRadius: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  topNavItem: {
    padding: "8px 16px",
    color: C.textMuted,
    textDecoration: "none",
    fontSize: 14,
    borderRadius: 6,
    whiteSpace: "nowrap",
    transition: "background 0.15s, color 0.15s",
  },
  topNavItemActive: {
    background: C.bgActive,
    color: C.text,
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px 4px",
    cursor: "pointer",
    color: "rgba(255,255,255,0.7)",
  },
  chevron: {
    transition: "transform 0.2s",
  },
};

// ─── Icons ──────────────────────────────────────────────────────────────────

function ChevronIcon({ direction }: { direction: "left" | "right" | "down" | "up" }) {
  const rotation = { left: 180, right: 0, down: 90, up: -90 }[direction];
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ transform: `rotate(${rotation}deg)`, transition: "transform 0.2s" }}
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// ─── NavItem Component ──────────────────────────────────────────────────────

function NavItem({
  item,
  isActive,
  collapsed,
}: {
  item: BuiltNavItem;
  isActive: boolean;
  collapsed?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const badge = item.props?.waiting_orders || item.props?.pending;

  return (
    <Link
      href={item.path}
      style={{
        ...styles.navItem,
        ...(isActive ? styles.navItemActive : {}),
        ...(hovered && !isActive ? styles.navItemHover : {}),
        justifyContent: collapsed ? "center" : "flex-start",
        padding: collapsed ? "10px" : "10px 16px",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={collapsed ? item.title : undefined}
    >
      {!collapsed && <span>{item.title}</span>}
      {!collapsed && badge && <span style={styles.badge}>{badge}</span>}
    </Link>
  );
}

// ─── NavGroup Component ─────────────────────────────────────────────────────

function NavGroup({
  group,
  currentPath,
  collapsed,
}: {
  group: BuiltNavGroup;
  currentPath: string;
  collapsed?: boolean;
}) {
  // Only "Quick Access" starts expanded; all other groups start collapsed.
  const [expanded, setExpanded] = useState(group.title === "Quick Access");

  if (collapsed) {
    // In collapsed mode, just show items as icons/initials
    return (
      <div style={styles.navSection}>
        {group.items.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            isActive={currentPath === item.path}
            collapsed
          />
        ))}
      </div>
    );
  }

  return (
    <div style={styles.navSection}>
      <div
        style={styles.groupHeader}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && setExpanded(!expanded)}
      >
        <span style={{ ...styles.groupTitle, padding: 0 }}>{group.title}</span>
        <ChevronIcon direction={expanded ? "down" : "right"} />
      </div>
      {expanded &&
        group.items.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            isActive={currentPath === item.path}
          />
        ))}
    </div>
  );
}

// ─── TopNavGroup Component ──────────────────────────────────────────────────
// Mirrors NavGroup's expand/collapse behaviour but renders horizontally as a
// dropdown button in the topbar.

function TopNavGroup({
  group,
  currentPath,
}: {
  group: BuiltNavGroup;
  currentPath: string;
}) {
  const [expanded, setExpanded] = useState(group.title === "Quick Access");
  const [hovered, setHovered] = useState(false);

  const hasActive = group.items.some((item) => currentPath === item.path);

  return (
    <div style={{ position: "relative" }}>
      {/* Group header button */}
      <button
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 12px",
          background: hasActive ? C.bgActive : hovered ? C.bgHover : "transparent",
          border: "none",
          borderRadius: 6,
          color: hasActive ? C.textActive : C.text,
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          transition: "background 0.15s, color 0.15s",
        }}
        aria-expanded={expanded}
      >
        {group.title}
        <ChevronIcon direction={expanded ? "up" : "down"} />
      </button>

      {/* Dropdown panel */}
      {expanded && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: C.bgSidebar,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            minWidth: 180,
            zIndex: 9999,
            boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
            padding: "4px 0",
          }}
        >
          {group.items.map((item) => (
            <TopNavDropdownItem
              key={item.id}
              item={item}
              isActive={currentPath === item.path}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TopNavDropdownItem({
  item,
  isActive,
}: {
  item: BuiltNavItem;
  isActive: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const badge = item.props?.waiting_orders || item.props?.pending;

  return (
    <Link
      href={item.path}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 16px",
        color: isActive ? C.textActive : C.text,
        textDecoration: "none",
        fontSize: 14,
        background: isActive ? C.bgActive : hovered ? C.bgHover : "transparent",
        transition: "background 0.15s, color 0.15s",
        whiteSpace: "nowrap",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ flex: 1 }}>{item.title}</span>
      {badge && <span style={styles.badge}>{badge}</span>}
    </Link>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function NavigationShell({
  layoutData,
  children,
  className,
  defaultCollapsed = false,
  position = "left",
}: NavigationShellProps) {
  const router = useRouter();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [router.asPath]);

  // Build navigation from layout data
  const navGroups = useMemo(() => {
    const panelAccess = layoutData?.panel_access ?? [];
    // If access includes "*", grant all panels
    const accessList = panelAccess.includes("*")
      ? ALL_PANELS
      : panelAccess.filter((p: string) => ALL_PANELS.includes(p));
    return buildNavigation(accessList, layoutData?.panel_access_data);
  }, [layoutData]);

  // Current path for active state
  const currentPath = router.asPath.split("?")[0];

  const techName = layoutData?.technician_name;
  const firstName = techName?.split(" ")[0] ?? null;

  // ─── Top Navigation ─────────────────────────────────────────────────────────

  if (position === "top") {
    return (
      <div style={styles.rootTop} className={className}>
        <nav style={styles.topbar}>
          {navGroups.map((group, i) => (
            <TopNavGroup
              key={group.title + i}
              group={group}
              currentPath={currentPath}
            />
          ))}
          {/* Spacer + user greeting pushed to right */}
          {firstName && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{
                width: 28, height: 28, borderRadius: "50%",
                background: C.bgActive,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, fontWeight: 700, color: "#fff",
                flexShrink: 0,
              }}>
                {firstName[0].toUpperCase()}
              </div>
              <span style={{ fontSize: 13, color: C.text, whiteSpace: "nowrap" }}>
                {firstName}
              </span>
            </div>
          )}
        </nav>
        <main style={styles.content}>
          <DataProvider name="navGroups" data={navGroups}>
            {children}
          </DataProvider>
        </main>
      </div>
    );
  }

  // ─── Nav content shared between sidebar and mobile drawer ───────────────────

  const navContent = (
    <>
      <div style={styles.header}>
        <span style={styles.logo}>Regrade</span>
        {isMobile ? (
          <button
            style={styles.drawerClose}
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        ) : (
          <button
            style={styles.collapseBtn}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <MenuIcon /> : <ChevronIcon direction="left" />}
          </button>
        )}
      </div>
      <nav>
        {navGroups.map((group, i) => (
          <NavGroup
            key={group.title + i}
            group={group}
            currentPath={currentPath}
            collapsed={!isMobile && collapsed}
          />
        ))}
      </nav>
      {/* User greeting — pinned to bottom of sidebar */}
      {firstName && !collapsed && (
        <div style={{
          marginTop: "auto",
          padding: "12px 16px",
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: C.bgActive,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: "#fff",
            flexShrink: 0,
          }}>
            {firstName[0].toUpperCase()}
          </div>
          <div style={{ overflow: "hidden" }}>
            <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Logged in as</div>
            <div style={{ fontSize: 13, color: C.textActive, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{techName}</div>
          </div>
        </div>
      )}
      {/* Collapsed state: just the avatar */}
      {firstName && collapsed && (
        <div style={{
          marginTop: "auto",
          padding: "12px 0",
          borderTop: `1px solid ${C.border}`,
          display: "flex",
          justifyContent: "center",
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            background: C.bgActive,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: "#fff",
          }}
            title={techName}
          >
            {firstName[0].toUpperCase()}
          </div>
        </div>
      )}
    </>
  );

  // ─── Mobile layout — topbar + slide-in drawer ────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ minHeight: "100vh", background: "#ffffff" }} className={className}>
        {/* Mobile topbar */}
        <header style={styles.mobileTopbar}>
          <button
            style={{ ...styles.collapseBtn, color: "rgba(255,255,255,0.7)" }}
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <MenuIcon />
          </button>
          <span style={styles.logo}>Regrade</span>
          {firstName ? (
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: C.bgActive,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: "#fff",
            }}
              title={techName}
            >
              {firstName[0].toUpperCase()}
            </div>
          ) : (
            <div style={{ width: 28 }} />
          )}
        </header>

        {/* Overlay */}
        {drawerOpen && (
          <div
            style={styles.overlay}
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Drawer */}
        <div
          style={{
            ...styles.drawer,
            transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
          }}
          aria-hidden={!drawerOpen}
        >
          {navContent}
        </div>

        <main style={styles.content}>
          <DataProvider name="navGroups" data={navGroups}>
            {children}
          </DataProvider>
        </main>
      </div>
    );
  }

  // ─── Desktop sidebar layout ──────────────────────────────────────────────────

  return (
    <div style={styles.root} className={className}>
      <aside
        style={{
          ...styles.sidebar,
          ...(collapsed ? styles.sidebarCollapsed : {}),
        }}
      >
        {navContent}
      </aside>

      <main style={styles.content}>
        <DataProvider name="navGroups" data={navGroups}>
          {children}
        </DataProvider>
      </main>
    </div>
  );
}

export default NavigationShell;
